import __request__ from 'routing/request';
interface CollectionUpdateRequest {
    readonly description: string;
    readonly title: string;
}
interface SharedCollectionListEntity {
    readonly description: string;
    readonly id: string;
    readonly title: string;
    readonly content: string;
    readonly manage: string;
}
interface CollectionListEntity {
    readonly shared_with_teams: number;
    readonly description: string;
    readonly id: string;
    readonly title: string;
    readonly shared_with_users: number;
}
interface BulkOperationFailure {
    readonly failure_explanation: string;
    readonly entity_id: string;
}
interface FilterOption {
    readonly title: string;
    readonly value: string;
}
type CollectionsResource__CollectionStatusArray = CollectionsResource__CollectionStatus[];
interface PaginatedResponse_CollectionDTO {
    readonly [_key: string]: Object;
}
interface CreateEntityRequest_CollectionDTO {
    readonly entity: CollectionDTO;
    readonly share_request: EntityShareRequest;
}
interface CollectionsResource__CollectionStatus {
    readonly id: string;
    readonly title: string;
}
interface EntityShareRequest {
    readonly selected_grantee_capabilities: {
        readonly [_key: string]: 'view' | 'manage' | 'own';
    };
    readonly selected_collections: string[];
}
interface CollectionsResource__CollectionStatusesRequest {
    readonly entity_grns: string[];
}
interface PaginatedList__PaginationInfo {
    readonly per_page: number;
    readonly total: number;
    readonly count: number;
    readonly page: number;
}
interface CollectionsResource__EntityUpdateRequest {
    readonly add_to: string[];
    readonly remove_from: string[];
}
interface PageListResponse_SharedCollectionListEntity {
    readonly total: number;
    readonly pagination: PaginatedList__PaginationInfo;
    readonly defaults: EntityDefaults;
    readonly query: string;
    readonly elements: SharedCollectionListEntity[];
    readonly attributes: EntityAttribute[];
    readonly sort: string;
    readonly order: 'asc' | 'desc';
}
type stringArray = string[];
type CollectionsResource__EntitySharingResponseArray = CollectionsResource__EntitySharingResponse[];
interface CollectionEntitiesListEntity {
    readonly name: string;
    readonly owners: Grantee[];
    readonly id: string;
    readonly type: string;
}
interface CollectionEntitiesUpdateRequest {
    readonly added_entities: string[];
    readonly removed_entities: string[];
}
interface BulkOperationResponse {
    readonly failures: BulkOperationFailure[];
    readonly successfully_performed: number;
    readonly errors: string[];
}
interface EntityAttribute {
    readonly filterable: boolean;
    readonly related_property: string;
    readonly related_collection: string;
    readonly hidden: boolean;
    readonly sortable: boolean;
    readonly id: string;
    readonly type: 'STRING' | 'DATE' | 'DOUBLE' | 'INT' | 'LONG' | 'OBJECT_ID' | 'BOOLEAN';
    readonly title: string;
    readonly searchable: boolean;
    readonly filter_options: FilterOption[];
}
interface CollectionsResource__EntitySharingResponse {
    readonly collection_id: string;
    readonly capability: 'view' | 'manage' | 'own';
    readonly collection_total: number;
    readonly grantee: string;
    readonly collection_name: string;
    readonly granteeName: string;
}
interface PageListResponse_CollectionEntitiesListEntity {
    readonly total: number;
    readonly pagination: PaginatedList__PaginationInfo;
    readonly defaults: EntityDefaults;
    readonly query: string;
    readonly elements: CollectionEntitiesListEntity[];
    readonly attributes: EntityAttribute[];
    readonly sort: string;
    readonly order: 'asc' | 'desc';
}
interface CollectionDTO {
    readonly entities: string[];
    readonly description: string;
    readonly id: string;
    readonly title: string;
}
interface PageListResponse_CollectionListEntity {
    readonly total: number;
    readonly pagination: PaginatedList__PaginationInfo;
    readonly defaults: EntityDefaults;
    readonly query: string;
    readonly elements: CollectionListEntity[];
    readonly attributes: EntityAttribute[];
    readonly sort: string;
    readonly order: 'asc' | 'desc';
}
interface Sorting {
    readonly id: string;
    readonly direction: 'ASC' | 'DESC';
}
interface Grantee {
    readonly id: string;
    readonly type: string;
    readonly title: string;
}
interface CollectionsResource__CollectionStatusArrayMap {
    readonly [_key: string]: CollectionsResource__CollectionStatusArray;
}
interface EntityDefaults {
    readonly sort: Sorting;
}
interface BulkOperationRequest {
    readonly entity_ids: string[];
}
/**
 * List all collections
 * @param page Page number to get
 * @param per_page Results per page
 * @param query Query string
 * @param sort The field to sort the result on
 * @param order The sort direction
 */
export function list(sort: 'title' | 'description' = 'title', page: number = 1, per_page: number = 50, query?: string, order: 'asc' | 'desc' = 'asc'): Promise<PaginatedResponse_CollectionDTO> {
    return __request__('GET', '/plugins/org.graylog.plugins.collections/collections', null, { 'page': page, 'per_page': per_page, 'query': query, 'sort': sort, 'order': order }, {
        'Accept': ['application/json']
    });
}
/**
 * Create a new collection
 */
export function create(request?: CreateEntityRequest_CollectionDTO): Promise<CollectionDTO> {
    return __request__('POST', '/plugins/org.graylog.plugins.collections/collections', request, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Delete a set of collections
 */
export function bulkDelete(Collectionstoremove: BulkOperationRequest): Promise<BulkOperationResponse> {
    return __request__('POST', '/plugins/org.graylog.plugins.collections/collections/bulk_delete', Collectionstoremove, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Provide information on sharing via collections for the given entity
 */
export function getCollectionSharing(entityGRN: string): Promise<CollectionsResource__EntitySharingResponseArray> {
    return __request__('GET', `/plugins/org.graylog.plugins.collections/collections/collection_sharing/${entityGRN}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Add a set of entities to a collection
 * @param collectionId collectionId
 */
export function bulkAdd(Entitiestoadd: BulkOperationRequest, collectionId?: string): Promise<BulkOperationResponse> {
    return __request__('POST', `/plugins/org.graylog.plugins.collections/collections/entities/bulk_add/${collectionId}`, Entitiestoadd, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Delete a set of entities from a collection
 * @param collectionId collectionId
 */
export function bulkDeleteBycollectionIdAndEntitiestoremove(Entitiestoremove: BulkOperationRequest, collectionId?: string): Promise<BulkOperationResponse> {
    return __request__('POST', `/plugins/org.graylog.plugins.collections/collections/entities/bulk_delete/${collectionId}`, Entitiestoremove, {}, {
        'Accept': ['application/json']
    });
}
/**
 * List all entities that can be added to the collection using paginated list format
 * @param collectionId collectionId
 * @param page Page number to get
 * @param per_page Results per page
 * @param query Query string
 * @param sort The field to sort the result on
 * @param order The sort direction
 */
export function getCandidateEntitiesPage(sort: 'name' | 'type' = 'name', collectionId?: string, page: number = 1, per_page: number = 50, query?: string, filters?: stringArray, order: 'asc' | 'desc' = 'asc'): Promise<PageListResponse_CollectionEntitiesListEntity> {
    return __request__('GET', `/plugins/org.graylog.plugins.collections/collections/entities/candidates/paginated/${collectionId}`, null, { 'page': page, 'per_page': per_page, 'query': query, 'filters': filters, 'sort': sort, 'order': order }, {
        'Accept': ['application/json']
    });
}
/**
 * List entities in a collection using paginated list format
 * @param collectionId collectionId
 * @param page Page number to get
 * @param per_page Results per page
 * @param query Query string
 * @param sort The field to sort the result on
 * @param order The sort direction
 */
export function getEntitiesPage(sort: 'name' | 'type' = 'name', collectionId?: string, page: number = 1, per_page: number = 50, query?: string, filters?: stringArray, order: 'asc' | 'desc' = 'asc'): Promise<PageListResponse_CollectionEntitiesListEntity> {
    return __request__('GET', `/plugins/org.graylog.plugins.collections/collections/entities/paginated/${collectionId}`, null, { 'page': page, 'per_page': per_page, 'query': query, 'filters': filters, 'sort': sort, 'order': order }, {
        'Accept': ['application/json']
    });
}
/**
 * List collections using paginated Entity List format
 * @param page Page number to get
 * @param per_page Results per page
 * @param query Query string
 * @param sort The field to sort the result on
 * @param order The sort direction
 * @param editable Returns only editable collections
 */
export function getPage(sort: 'title' | 'description' | 'shared_with_users' | 'shared_with_teams' = 'title', page: number = 1, per_page: number = 50, query?: string, order: 'asc' | 'desc' = 'asc', editable: boolean = false): Promise<PageListResponse_CollectionListEntity> {
    return __request__('GET', '/plugins/org.graylog.plugins.collections/collections/paginated', null, { 'page': page, 'per_page': per_page, 'query': query, 'sort': sort, 'order': order, 'editable': editable }, {
        'Accept': ['application/json']
    });
}
/**
 * List collections for a team or user.
 * @param page Page number to get
 * @param per_page Results per page
 * @param query Query string
 * @param sort The field to sort the result on
 * @param order The sort direction
 */
export function getSharedPaginated(entityId: string, entity_type: 'team' | 'user', sort: 'title' | 'description' | 'manage' | 'content' = 'title', page: number = 1, per_page: number = 50, query?: string, filters?: stringArray, order: 'asc' | 'desc' = 'asc'): Promise<PageListResponse_SharedCollectionListEntity> {
    return __request__('GET', `/plugins/org.graylog.plugins.collections/collections/shares/${entityId}/paginated`, null, { 'entity_type': entity_type, 'page': page, 'per_page': per_page, 'query': query, 'filters': filters, 'sort': sort, 'order': order }, {
        'Accept': ['application/json']
    });
}
/**
 * Get collection membership data for a list of entity ids
 */
export function getStatuses(entity_grns: CollectionsResource__CollectionStatusesRequest): Promise<CollectionsResource__CollectionStatusArrayMap> {
    return __request__('POST', '/plugins/org.graylog.plugins.collections/collections/status', entity_grns, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Update a collection
 * @param collectionId collectionId
 * @param collection collection
 */
export function update(collectionId?: string, collection?: CollectionUpdateRequest): Promise<CollectionDTO> {
    return __request__('PUT', `/plugins/org.graylog.plugins.collections/collections/${collectionId}`, collection, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get a collection
 * @param collectionId collectionId
 */
export function get(collectionId?: string): Promise<CollectionDTO> {
    return __request__('GET', `/plugins/org.graylog.plugins.collections/collections/${collectionId}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Delete a collection
 * @param collectionId collectionId
 */
export function remove(collectionId?: string): Promise<void> {
    return __request__('DELETE', `/plugins/org.graylog.plugins.collections/collections/${collectionId}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Update collection entities
 * @param collectionId collectionId
 * @param updaterequest update request
 */
export function updateEntities(collectionId?: string, updaterequest?: CollectionEntitiesUpdateRequest): Promise<CollectionDTO> {
    return __request__('PUT', `/plugins/org.graylog.plugins.collections/collections/${collectionId}/entities`, updaterequest, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Add/remove an entity in multiple collections
 */
export function updateCollections(entityGRN: string, request: CollectionsResource__EntityUpdateRequest): Promise<void> {
    return __request__('PUT', `/plugins/org.graylog.plugins.collections/collections/${entityGRN}/collections`, request, {}, {
        'Accept': ['application/json']
    });
}
